<template>
    <div>
        <b-row>
            <b-col cols="12">
                <ValidationObserver ref="documentFormValidate">
                    <b-table
                        :items="documentType"
                        :fields="documentFields"
                        bordered
                        responsive
                        show-empty
                        :empty-text="$t('no_result')">
                        <template #cell(document_type)="row">
                            {{ getLocaleField(row.item.name) }}
                            <b-badge v-if="row.item.code !== 'other_document'" variant="danger" class="ml-1">
                                {{ $t('required') }}
                            </b-badge>
                        </template>
                        <template #cell(file)="row">
                            <ValidationProvider
                                :name="row.item.code"
                                :rules="isUploaded(row.item.code) ? '' : 'required'"
                                v-slot="{errors}">
                                <input type="hidden" v-model="localFormData.documents[row.item.code]" />
                                <div
                                    class="file-upload-area"
                                    :class="{ 'is-invalid': errors[0], 'has-file': (localFormData.documents && localFormData.documents[row.item.code]) || isUploaded(row.item.code) }"
                                    @click="!isReadOnly && !isUploaded(row.item.code) && triggerFileInput(row.item.code)">

                                    <div class="upload-icon">
                                        <i :class="(localFormData.documents && localFormData.documents[row.item.code]) || isUploaded(row.item.code) ? 'ri-file-text-line' : 'ri-upload-cloud-2-line'"></i>
                                    </div>

                                    <div class="upload-text">
                                        <div v-if="isUploaded(row.item.code) && !localFormData.documents[row.item.code]" class="file-info uploaded">
                                            <span class="file-name text-success">{{ $t('already_uploaded') }}</span>
                                            <div class="d-flex align-items-center mt-1">
                                                <b-button variant="link" size="sm" @click.native.stop="downloadFile(row.item.code)" class="p-0 text-primary mr-2">
                                                    <i class="ri-download-2-line"></i> {{ $t('download') }}
                                                </b-button>
                                            </div>
                                        </div>
                                        <div v-else-if="localFormData.documents && localFormData.documents[row.item.code]" class="file-info">
                                            <span class="file-name text-primary font-weight-bold">{{ $t('new_file_selected') }}</span>
                                            <span class="small text-muted">{{ localFormData.documents[row.item.code].name }}</span>
                                            <span class="text-info small mt-1"><i class="ri-information-line"></i> {{ $t('click_save_to_upload') }}</span>
                                        </div>
                                        <div v-else-if="isRejected(row.item.code)" class="file-info rejected">
                                            <b-badge variant="danger" class="mb-1">{{ $t('rejected') }}</b-badge>
                                            <span class="text-danger small d-block mb-2">{{ $t('document_rejected_reupload_info') }}</span>
                                            <span class="placeholder-text">
                                            {{ getLocaleField(row.item.name) }} {{ $t('click_to_reupload') }}
                                        </span>
                                        </div>
                                        <span v-else class="placeholder-text">
                                        {{ getLocaleField(row.item.name) }} {{ $t('click_to_select') }}
                                    </span>
                                    </div>
                                    <input
                                        type="file"
                                        :ref="`fileInput_${row.item.code}`"
                                        style="display: none"
                                        @change="handleFileChange($event, row.item.code)"
                                        :accept="'.pdf,.doc,.docx'"
                                    />
                                </div>
                                <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"/>
                            </ValidationProvider>
                        </template>

                    </b-table>
                </ValidationObserver>
            </b-col>
        </b-row>


        <div class="d-flex justify-content-center mt-3">
            <processing-button
                :processing="loading"
                variant="primary"
                :label="applicationButtonTitle"
                @click="saveDocuments"
            />
        </div>
    </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import BisepService from "@/services/BisepService";
import ProcessingButton from "@/components/elements/ProcessingButton.vue";

export default {
    name: "DocumentsTab",
    components: {
        ProcessingButton,
        ValidationObserver,
        ValidationProvider
    },
    props: {
        footerInfo: {
            type: String,
            default: ''
        },
        isReadOnly: {
            type: Boolean,
            default: false
        },
        showOnlyRejected: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            loading: false,
            formData: {},
            localFormData: {
                documents: {}
            },
            documentFields: [
                { key: 'document_type', label: this.$t('document_type') },
                { key: 'file', label: this.$t('file') },
                { key: 'sample_document', label: this.$t('sample_document') },
            ]
        }
    },
    computed: {
        documentType() {
            const docs = this.$store.getters.getParameter('bisep_applications.documents') || [];
            if (this.showOnlyRejected) {
                return docs.filter(doc => this.isRejected(doc.code));
            }
            return docs;
        },
        isAllDocumentsUploaded() {
            const passingScores = Object.fromEntries(
                Object.entries(this.localFormData.documents)
                    .filter(([, value]) => value === null)
            )
            const keys=Object.keys(passingScores)
            if (!keys.length){
                return  true
            }
            if (keys.length === 1 && keys.includes('other_document')){
                return  true
            }
            return false
        },
        applicationButtonTitle() {
            return this.formData.status === 'waiting_approve'
                ? this.$t('save').toUpper()
                : this.$t('complete_application').toUpper()
        },

    },
    async mounted() {
        await this.$store.dispatch("fetchParameter", {
            parameterCode: "bisep_applications.documents",
            isPublic: true,
            sort: '-rank'
        });
        if (this.isReadOnly) {
            this.documentFields = this.documentFields.filter(f => f.key !== 'actions');
        }
        this.setDocuments();
    },
    methods: {
        isUploaded(code) {
            return this.formData.documents && this.formData.documents[code] === 'uploaded';
        },
        isRejected(code) {
            return this.formData.documents && this.formData.documents[code] === 'declined';
        },
        triggerFileInput(code) {
            const refName = `fileInput_${code}`
            const input = this.$refs[refName]
            if (input) {
                if (Array.isArray(input)) {
                    input[0].click()
                } else {
                    input.click()
                }
            }
        },
        handleFileChange(event, code) {
            const file = event.target.files[0]
            if (file) {
                this.$set(this.localFormData.documents, code, file)
            }
        },
        getLocaleField(field) {
            if (typeof field === 'object' && field !== null) {
                return field[this.$i18n.locale] || field.tr || field.en || field
            }
            return field
        },
        setDocuments() {
            if (!this.documentType) return
            this.documentType.forEach(slotStatus => {
                this.$set(this.localFormData.documents, slotStatus.code, null)
            })
        },
        validate() {
            return this.isAllDocumentsUploaded
        },
        async saveDocuments() {
            if (this.isAllDocumentsUploaded) {
            this.$emit('nextTab',{
                currentTab: 2,
                formData: {
                    documents:this.localFormData?.documents
                },
                save:true
            })
            } else {
                this.$toast.warning(this.$t('please_upload_required_documents_error'))
            }
        },
        downloadFile(type) {
            const data = {
                national_id: this.formData.national_id,
                pin: this.getPin(),
                type: type
            }
            BisepService.downloadFile(data).then(response => {

                this._downloadFile(response, type+'.'+(response.headers.file_extension))
            }).catch(e => {
                this.showErrors(e, null, true)
            })
        },
    }
};
</script>
